import _typeof from "@babel/runtime/helpers/typeof";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

/* any functionality that's shared between component variants but not needed by external components goes in this file */
import { useState, useRef, useImperativeHandle, useEffect } from 'react';
import { debounce } from '@chegg-ui/utils';
import { findOptionByValue } from '@chegg-ui/listbox';

var lookupValueInOptions = function lookupValueInOptions(query, options, onFindOption) {
  var foundIndex = options.findIndex(function (option) {
    var optionValue = typeof option === 'string' ? option : option.value;
    return optionValue.toLowerCase().indexOf(query.toLowerCase()) === 0;
  });

  if (~foundIndex) {
    onFindOption(foundIndex);
  }
};

var debouncedLookup = debounce(lookupValueInOptions, 300);
export function useDropdownBehavior(_ref) {
  var ref = _ref.ref,
      value = _ref.value,
      options = _ref.options,
      disabled = _ref.disabled,
      onSelectValue = _ref.onSelectValue;
  var listBoxRef = useRef(null);
  var fauxInputRef = useRef(null);
  /* istanbul ignore next */

  useImperativeHandle(ref, function () {
    return fauxInputRef.current;
  }); // Track focused value from mouse events
  // For mouse events, the component uses css :hover to show focus

  var _useState = useState(0),
      _useState2 = _slicedToArray(_useState, 2),
      pseudoFocusedListboxIndex = _useState2[0],
      setPseudoFocusedListboxIndex = _useState2[1];

  var _useState3 = useState(-1),
      _useState4 = _slicedToArray(_useState3, 2),
      selectedListboxIndex = _useState4[0],
      setSelectedListboxIndex = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      isDropdownOpen = _useState6[0],
      setIsDropdownOpen = _useState6[1];

  var _useState7 = useState(''),
      _useState8 = _slicedToArray(_useState7, 2),
      quickSearchQuery = _useState8[0],
      setQuickSearchQuery = _useState8[1]; // unlike the input element itself, our FauxInputButton does _not_ receive the css:hover state from the label,
  // so we're forced to track this hover behavior ourselves in order to replicate the behavior of other inputs


  var _useState9 = useState(false),
      _useState10 = _slicedToArray(_useState9, 2),
      isLabelHovered = _useState10[0],
      setIsLabelHovered = _useState10[1]; // we clear the query whenever the pseudofocus index changes, which happens when
  // the user stops typing or uses other keys to interact with the listbox


  useEffect(function () {
    setQuickSearchQuery('');
  }, [pseudoFocusedListboxIndex]);
  useEffect(function () {
    findOptionByValue(value, options, function (foundIndex) {
      return setSelectedListboxIndex(foundIndex);
    });
  }, [value]); // if the value changes, that value (or 0 if no value) should be the pseudofocused index

  useEffect(function () {
    lookupValueInOptions(value, options, function (foundIndex) {
      return setPseudoFocusedListboxIndex(foundIndex);
    });
  }, [value]);

  var openAndFocusListbox = function openAndFocusListbox() {
    setIsDropdownOpen(true); // we need the setTimeout to force the func to run on next event loop after UL has rendered in

    setTimeout(function () {
      /* istanbul ignore else */
      if (listBoxRef.current) {
        listBoxRef.current.focus();
      }
    });
  };

  var closeAndFocusFauxInput = function closeAndFocusFauxInput() {
    setIsDropdownOpen(false);
    /* istanbul ignore else */

    if (fauxInputRef.current) {
      fauxInputRef.current.focus();
    }
  };

  var onFauxInputClick = function onFauxInputClick(event) {
    event.preventDefault();

    if (!disabled) {
      if (isDropdownOpen) {
        closeAndFocusFauxInput();
      } else {
        openAndFocusListbox();
      }
    }
  };

  var onFauxInputMouseEnter = function onFauxInputMouseEnter(event) {
    event.preventDefault();

    if (!disabled) {
      if (!isDropdownOpen) {
        openAndFocusListbox();
      }
    }
  };

  var onFauxInputKeyDown = function onFauxInputKeyDown(event) {
    if (disabled) return;

    switch (event.key) {
      case 'ArrowUp':
      case 'Up':
        {
          decrementPseudoIndex();
          openAndFocusListbox();
          break;
        }

      case 'ArrowDown':
      case 'Down':
        {
          incrementPseudoIndex();
          openAndFocusListbox();
          break;
        }

      case 'Enter':
      case ' ':
      case 'Space':
        openAndFocusListbox();
        break;

      default:
        return;
    }

    event.preventDefault();
  };

  var onListboxBlur = function onListboxBlur() {
    setIsDropdownOpen(false);
    setPseudoFocusedListboxIndex(0);
  };

  var decrementPseudoIndex = function decrementPseudoIndex() {
    var nextFocus = pseudoFocusedListboxIndex - 1; // don't cycle from the start to the end

    if (nextFocus < 0) {
      nextFocus = 0;
    }

    setPseudoFocusedListboxIndex(nextFocus);
  };

  var incrementPseudoIndex = function incrementPseudoIndex() {
    var nextFocus = pseudoFocusedListboxIndex + 1; // don't cycle from the end to the start

    if (nextFocus > options.length - 1) {
      nextFocus = options.length - 1;
    }

    setPseudoFocusedListboxIndex(nextFocus);
  };

  var onListboxKeyDown = function onListboxKeyDown(event) {
    switch (event.key) {
      case 'ArrowUp':
      case 'Up':
        {
          decrementPseudoIndex();
          break;
        }

      case 'ArrowDown':
      case 'Down':
        {
          incrementPseudoIndex();
          break;
        }

      case 'Enter':
        onSelectListboxOption(pseudoFocusedListboxIndex);
        break;

      case 'Escape':
      case 'Esc':
        closeAndFocusFauxInput();
        break;

      case 'Home':
        setPseudoFocusedListboxIndex(0);
        break;

      case 'End':
        setPseudoFocusedListboxIndex(options.length - 1);
        break;

      default:
        // we only want to handle "printable" characters and ignore others https://stackoverflow.com/a/49612427
        if (event.key && event.key.length === 1 && !event.metaKey) {
          var newQuery = "".concat(quickSearchQuery).concat(event.key.toLowerCase());
          setQuickSearchQuery(newQuery);
          debouncedLookup(newQuery, options, setPseudoFocusedListboxIndex);
          break;
        } // return so preventDefault doesn't affect any other keys


        return;
    }

    event.stopPropagation();
    event.preventDefault();
  };

  var displayedValue = value;

  if (_typeof(options[0]) === 'object') {
    var findOption = options.find(function (o) {
      return o.value === value;
    });
    if (findOption) displayedValue = findOption.child;
  }
  /*
   * Sets the dropdown selected value when user clicks or presses enter on option.
   * Triggers parent onChange event and blurs focus on dropdown
   */


  var onSelectListboxOption = function onSelectListboxOption(selectedOptionIndex) {
    var selectedOption = options[selectedOptionIndex];
    onSelectValue(typeof selectedOption === 'string' ? selectedOption : selectedOption.value);
    closeAndFocusFauxInput();
  };

  return {
    displayedValue: displayedValue,
    fauxInputRef: fauxInputRef,
    isDropdownOpen: isDropdownOpen,
    isLabelHovered: isLabelHovered,
    listBoxRef: listBoxRef,
    onFauxInputClick: onFauxInputClick,
    onFauxInputKeyDown: onFauxInputKeyDown,
    onFauxInputMouseEnter: onFauxInputMouseEnter,
    onLabelMouseEnter: function onLabelMouseEnter() {
      return setIsLabelHovered(true);
    },
    onLabelMouseLeave: function onLabelMouseLeave() {
      return setIsLabelHovered(false);
    },
    onListboxBlur: onListboxBlur,
    onListboxKeyDown: onListboxKeyDown,
    onSelectListboxOption: onSelectListboxOption,
    pseudoFocusedListboxIndex: pseudoFocusedListboxIndex,
    selectedListboxIndex: selectedListboxIndex
  };
}